/* cSpell:words WFES */
import { useEffect } from 'react';
import { gaPageView, gaEvent } from '@domgen/shared/services/ga/ga';
import { getSystemCode } from '@domgen/whirlpool-olr/src/utils/getSystemCode';
import { usePostHog } from '@posthog/react';
import { useAppState } from '@domgen/whirlpool-olr/src/store/selectors/useAppState';
import { useUrlParams } from '@domgen/whirlpool-olr/src/store/selectors/useUrlParams';

export const TwoYearPartsPage = () => {
  const { session } = useAppState();
  const urlParams = useUrlParams();
  const systemCode = getSystemCode(urlParams?.rs);
  const posthog = usePostHog();

  useEffect(() => {
    gaPageView({
      category: 'registration-pages',
      type: 'STANDARD',
      regSystemCode: systemCode,
      appSessionID: session?.id,
      pagename: 'olr-2yearparts',
    });
  }, [systemCode, session?.id]);

  const handlePhoneLinkClicked = () => {
    gaEvent({
      eventCategory: 'registration-pages',
      eventAction: 'click',
      eventLabel: '2yearparts_phone_number',
      category: 'registration-pages',
      type: 'STANDARD',
      regSystemCode: systemCode,
      appSessionID: session?.id,
      pagename: 'olr-2yearparts',
    });
    posthog.capture('2-year-parts-contact-clicked');
  };

  const handleWebsiteLinkClicked = () => {
    gaEvent({
      eventCategory: 'registration-pages',
      eventAction: 'click',
      eventLabel: '2yearparts_website',
      category: 'registration-pages',
      type: 'STANDARD',
      regSystemCode: systemCode,
      appSessionID: session?.id,
      pagename: 'olr-2yearparts',
    });
    posthog.capture('2-year-parts-website-clicked');
  };

  return (
    <div className="max-w-300 mx-auto p-space-700">
      <h1 className="text-h3 pb-5">2-Year Limited Parts Warranty - Terms</h1>
      <div className="flex flex-col gap-space-200">
        <p>
          2-Year Limited Parts Warranty: 1-year manufacturer’s limited warranty + 1 year additional
          free parts
        </p>
        <p className="flex flex-col gap-space-200">
          Register now for this offer:{' '}
          <a
            onClick={handlePhoneLinkClicked}
            className="text-body-link-strong hover:text-body-strong"
            href="tel:888-900-3381"
          >
            888-900-3381
          </a>{' '}
          or by visiting{' '}
          <a
            onClick={handleWebsiteLinkClicked}
            target="_blank"
            rel="noopener noreferrer"
            className="text-body-link-strong hover:text-body-strong"
            href="https://www.whirlpool.com/register"
          >
            whirlpool.com/register
          </a>
          <a
            onClick={handleWebsiteLinkClicked}
            target="_blank"
            rel="noopener noreferrer"
            className="text-body-link-strong hover:text-body-strong"
            href="https://www.amana.com/register"
          >
            amana.com/register
          </a>
          <a
            onClick={handleWebsiteLinkClicked}
            target="_blank"
            rel="noopener noreferrer"
            className="text-body-link-strong hover:text-body-strong"
            href="https://www.maytag.com/register"
          >
            maytag.com/register
          </a>
          <a
            onClick={handleWebsiteLinkClicked}
            target="_blank"
            rel="noopener noreferrer"
            className="text-body-link-strong hover:text-body-strong"
            href="https://www.kitchenaid.com/register"
          >
            kitchenaid.com/register
          </a>
        </p>
        <p>(Lines open 8am-8pm, Monday to Friday, and 9am-6pm Saturday EST and Sunday EST)</p>
      </div>

      <h2 className="text-body-large-strong pt-5 mb-2">Which offer terms apply to me?</h2>
      <h2 className="text-body-large-strong mb-5">
        Most customers should use the Standard Offer Terms.
      </h2>

      <div className="flex flex-col gap-space-200">
        <p>
          If you recently received this offer with a Whirlpool, Maytag, Amana or KitchenAid
          appliance, your appliance may be eligible under the current Standard Offer. The two model
          numbers listed later on this page relate only to an earlier limited trial and are{' '}
          <strong>not</strong> a complete list of models eligible for the current Standard Offer.
        </p>
      </div>

      <h2 className="text-body-large-strong py-5">
        The Trial Offer Terms are only for an earlier limited trial.
      </h2>
      <div className="flex flex-col gap-space-200">
        <p>
          Use the Trial Offer Terms only if you received earlier trial offer materials for one of
          the two models listed in that section.
        </p>
      </div>

      <h2 className="text-body-large-strong pt-5 mb-2">
        Standard Offer Terms – Current Offer for Most Customers
      </h2>
      <h2 className="text-body-large-strong mb-5">
        For eligible appliances from participating Whirlpool Corporation brands, including
        Whirlpool, Maytag, Amana and KitchenAid.
      </h2>

      <div className="flex flex-col gap-space-200">
        <p>
          Offer valid for first-time registration of eligible Whirlpool Corporation appliances
          participating in this offer. Product registration must be completed within 60 days of
          appliance delivery and before 12/31/2026 to qualify.
        </p>
        <p>
          This offer provides an additional 1-year limited parts coverage, beginning upon expiration
          of the manufacturer’s standard 1-year limited warranty, for a total of 2 years of limited
          parts coverage from the date the manufacturer’s warranty begins. All original warranty
          terms, conditions, and exclusions apply to this limited parts warranty extension, except
          for labor and the duration of coverage.
        </p>{' '}
        <p>
          Parts or models with existing warranties longer than 1 year are excluded and keep their
          original warranty terms. Excludes countertop appliances and products that are already
          registered. Limited parts offer and coverage is transferable to subsequent owners. This
          offer applies to the following branded appliances: Amana, Whirlpool, Maytag, and
          KitchenAid.
        </p>{' '}
        <p>
          Registration is not required for the manufacturer’s standard warranty, which begins on the
          date of delivery and is not impacted by this offer. Failure to register does not diminish
          your standard warranty rights. Already registered products do not qualify for this offer.
          California residents are not required to register to obtain the additional 1-year limited
          parts coverage.
        </p>{' '}
        <p>
          Must use Whirlpool Service Network to install covered replacement parts. If you have other
          applicable coverage, such as an extended service plan, you must contact the provider of
          that coverage to receive those benefits, which may be mutually exclusive with this offer.
          For service, contact the Whirlpool Customer Experience Center at{' '}
          <a
            onClick={handlePhoneLinkClicked}
            className="text-body-link-strong hover:text-body-strong"
            href="tel:800-253-1301"
          >
            800-253-1301
          </a>
          .
        </p>
      </div>

      <h2 className="text-body-large-strong pt-5 mb-2">
        Trial Offer Terms - Earlier Limited Trial Only
      </h2>
      <h2 className="text-body-large-strong mb-5">
        Only use this section if you received earlier trial offer materials for one of the models
        listed below purchased between 6/26/2025 and 4/20/2026. These model numbers are not a
        complete list of products eligible for the current Standard Offer.
      </h2>

      <div className="flex flex-col gap-space-200">
        <p>
          Offer valid for first-time registration of the following eligible Whirlpool® appliance
          models that included promotional materials as part of a limited trial:
        </p>
        <ul className="list-disc list-inside">
          <li>WED5010LW</li>
          <li>WFES3030RS</li>
        </ul>

        <p>
          Registration must be completed by calling{' '}
          <a
            onClick={handlePhoneLinkClicked}
            className="text-body-link-strong hover:text-body-strong"
            href="tel:888-900-3381"
          >
            888-900-3381
          </a>{' '}
          and before 12/31/2026 to qualify.
        </p>
        <p>
          This offer provides an additional 1-year limited parts coverage, beginning upon expiration
          of the manufacturer’s standard 1-year limited warranty, for a total of 2 years of limited
          parts coverage from the date the manufacturer’s warranty begins. This offer is not an
          extension of the manufacturer’s warranty, and failure to register does not impact your
          warranty rights. Registration is not required for the manufacturer’s standard warranty,
          which begins on the date of delivery and is not impacted by this offer. Already registered
          products, including those auto-registered through online purchases, do not qualify for
          this offer. California residents are not required to register to obtain the additional
          1-year limited parts coverage. Limited parts offer and coverage is transferable to
          subsequent owners. Must use Whirlpool Service Network to install covered replacement
          parts. If you have other applicable coverage, such as an extended service plan, you must
          contact the provider of that coverage to receive those benefits, which may be mutually
          exclusive with this offer. For service, contact the Whirlpool Customer Experience Center
          using the contact information provided with your standard warranty documentation.
        </p>
      </div>
    </div>
  );
};
